import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, PageHeader, Alert, Button, EmptyState, Icon } from '../ui';
import { useTranslation } from '../context/TranslationContext';
import { useModules } from '../modules/useModules';
import styles from './Reports.module.css';

/**
 * Reports — a docs-style library layout.
 *
 * Replaced the older dual-row tab UI (underline module tabs + section pills)
 * that hid module switching behind subtle tabs. Now every report across every
 * module is visible at once in the left rail, grouped by module heading. The
 * right pane shows the active report. On narrow viewports the rail collapses
 * into a toggleable drawer to keep content full-width on phones.
 */
export default function Reports() {
  const [authError, setAuthError] = useState(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const navigate = useNavigate();
  const { moduleId, sectionKey } = useParams();
  const { t } = useTranslation();
  const { modules } = useModules();
  const activeBtnRef = useRef(null);

  const moduleGroups = useMemo(() => {
    return modules
      .filter((m) => (m.reportSections || []).length > 0)
      .map((m) => ({
        id: m.id,
        displayNameKey: m.displayNameKey,
        icon: m.icon || 'fa-chart-bar',
        order: m.order || 0,
        sections: [...(m.reportSections || [])].sort(
          (a, b) => (a.order || 0) - (b.order || 0)
        ),
      }))
      .sort((a, b) => a.order - b.order);
  }, [modules]);

  const activeGroup = moduleGroups.find((g) => g.id === moduleId) || null;
  const activeSection =
    activeGroup?.sections.find((s) => s.key === sectionKey) || null;
  const ActiveComponent = activeSection?.component || null;

  // Keep URL pointed at a valid module/section; default to first available.
  useEffect(() => {
    if (moduleGroups.length === 0) return;
    if (activeGroup && activeSection) return;
    const fallback = moduleGroups[0];
    navigate(`/reports/${fallback.id}/${fallback.sections[0].key}`, {
      replace: true,
    });
  }, [moduleGroups, activeGroup, activeSection, navigate]);

  useEffect(() => {
    const handler = (e) => setAuthError(e.detail);
    window.addEventListener('auth-error', handler);
    return () => window.removeEventListener('auth-error', handler);
  }, []);

  // Scroll active rail entry into view (helps when there are many reports).
  useEffect(() => {
    if (activeBtnRef.current) {
      activeBtnRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [moduleId, sectionKey]);

  const handlePick = (mId, sKey) => {
    navigate(`/reports/${mId}/${sKey}`);
    setMobileNavOpen(false);
  };

  const activeLabel = activeSection ? t(activeSection.labelKey) : null;
  const activeModuleLabel = activeGroup ? t(activeGroup.displayNameKey) : null;

  return (
    <Container>
      <PageHeader title={t('reports')} />

      {authError && (
        <Alert variant="warning" className={styles.errorAlert}>
          <div>
            <div className={styles.errorTitle}>{t('error')}</div>
            <p className={styles.errorBody}>{authError.message}</p>
          </div>
          <div className={styles.errorAction}>
            <Button variant="secondary" size="sm" onClick={() => navigate('/login')}>
              {t('login')}
            </Button>
          </div>
        </Alert>
      )}

      {moduleGroups.length === 0 ? (
        <p className={styles.muted}>{t('loading')}</p>
      ) : (
        <div className={styles.layout}>
          {/* Mobile picker — replaces the rail on narrow viewports. */}
          <button
            type="button"
            className={styles.mobilePicker}
            onClick={() => setMobileNavOpen((v) => !v)}
            aria-expanded={mobileNavOpen}
          >
            <Icon name={activeGroup?.icon || 'fa-chart-bar'} size="sm" />
            <span className={styles.mobilePickerLabel}>
              {activeModuleLabel && (
                <span className={styles.mobilePickerModule}>
                  {activeModuleLabel}
                </span>
              )}
              <span className={styles.mobilePickerSection}>
                {activeLabel || t('reports.selectReport')}
              </span>
            </span>
            <Icon
              name="fa-chevron-down"
              size="sm"
              className={`${styles.mobilePickerChevron} ${
                mobileNavOpen ? styles.mobilePickerChevronOpen : ''
              }`}
            />
          </button>

          <nav
            className={`${styles.rail} ${
              mobileNavOpen ? styles.railOpenMobile : ''
            }`}
            aria-label={t('reports.allReports')}
          >
            <div className={styles.railInner}>
              {moduleGroups.map((g) => (
                <div key={g.id} className={styles.railGroup}>
                  <div className={styles.railGroupHeader}>
                    <Icon
                      name={g.icon}
                      size="sm"
                      className={styles.railGroupIcon}
                    />
                    <span>{t(g.displayNameKey)}</span>
                  </div>
                  <ul className={styles.railList}>
                    {g.sections.map((s) => {
                      const isActive =
                        moduleId === g.id && sectionKey === s.key;
                      return (
                        <li key={s.key}>
                          <button
                            type="button"
                            ref={isActive ? activeBtnRef : null}
                            className={`${styles.railLink} ${
                              isActive ? styles.railLinkActive : ''
                            }`}
                            onClick={() => handlePick(g.id, s.key)}
                            aria-current={isActive ? 'page' : undefined}
                          >
                            {s.icon && (
                              <Icon
                                name={s.icon}
                                size="sm"
                                className={styles.railLinkIcon}
                              />
                            )}
                            <span className={styles.railLinkText}>
                              {t(s.labelKey)}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          </nav>

          <section className={styles.pane}>
            {activeSection && (
              <div className={styles.paneHeader}>
                <div className={styles.paneBreadcrumb}>
                  <Icon
                    name={activeGroup.icon}
                    size="sm"
                    className={styles.paneBreadcrumbIcon}
                  />
                  <span className={styles.paneBreadcrumbModule}>
                    {activeModuleLabel}
                  </span>
                  <Icon
                    name="fa-chevron-right"
                    size="sm"
                    className={styles.paneBreadcrumbDivider}
                  />
                  <span className={styles.paneBreadcrumbSection}>
                    {activeLabel}
                  </span>
                </div>
              </div>
            )}

            <div className={styles.paneBody}>
              {ActiveComponent ? (
                <ActiveComponent />
              ) : (
                <EmptyState
                  icon="fa-chart-line"
                  title={t('reports.emptyTitle')}
                  description={t('reports.emptyDescription')}
                />
              )}
            </div>
          </section>
        </div>
      )}
    </Container>
  );
}
